<template>
  <div class="report-page">
    <div class="report-scroll">
    <section class="report-card report-toolbar-card">
      <div class="toolbar-top">
        <div class="quick-actions">
          <el-button
            v-for="item in quickRangeOptions"
            :key="item.value"
            size="small"
            :type="quickRange === item.value ? 'primary' : 'default'"
            :plain="quickRange !== item.value"
            @click="handleQuickRangeChange(item.value)"
          >
            {{ item.label }}
          </el-button>
          <el-date-picker
            v-model="dateRange"
            type="daterange"
            unlink-panels
            value-format="timestamp"
            range-separator="-"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            class="toolbar-date"
            @change="handleCustomDateChange"
          />
        </div>

        <div class="toolbar-actions">
          <el-button
            size="small"
            icon="el-icon-refresh"
            :loading="loading"
            @click="fetchReport"
          >
            刷新
          </el-button>
          <el-button
            size="small"
            type="primary"
            icon="el-icon-download"
            :disabled="!tableRows.length"
            @click="handleExport"
          >
            导出报表
          </el-button>
        </div>
      </div>

      <div class="toolbar-filters">
        <div class="filter-label">
          <span>筛选</span>
          <i class="el-icon-edit-outline"></i>
        </div>

        <div class="filter-grid">
          <el-select
            v-model="selectedApps"
            filterable
            multiple
            collapse-tags
            placeholder="应用：请选择"
            class="toolbar-field toolbar-field--compact"
          >
            <el-option
              v-for="item in appOptions"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>

          <el-select
            v-model="selectedAdFormats"
            filterable
            multiple
            collapse-tags
            placeholder="广告类型：请选择"
            class="toolbar-field toolbar-field--compact"
          >
            <el-option
              v-for="item in adTypeOptions"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>

          <el-select
            v-model="filters.adsource_id_list"
            clearable
            filterable
            allow-create
            default-first-option
            placeholder="广告源：请选择"
            class="toolbar-field toolbar-field--compact"
          >
            <el-option label="Pangle" value="pangle" />
            <el-option label="AdMob" value="admob" />
            <el-option label="Unity Ads" value="unity_ads" />
            <el-option label="Mintegral" value="mintegral" />
          </el-select>
        </div>
      </div>

      <div class="toolbar-preview">
        <div class="preview-title">数据预览</div>
        <div class="preview-tools">
          <button class="preview-icon is-active">
            <i class="el-icon-menu"></i>
          </button>
          <button class="preview-icon">
            <i class="el-icon-data-analysis"></i>
          </button>
          <button class="preview-icon">
            <i class="el-icon-pie-chart"></i>
          </button>
          <button class="preview-icon">
            <i class="el-icon-s-marketing"></i>
          </button>
          <el-switch
            v-model="showDataShadow"
            active-text="显示数据阴影"
            inactive-text=""
          />
        </div>
      </div>

      <div class="toolbar-tags">
        <div class="tag-row">
          <div class="tag-label">维度</div>
          <div class="tag-list">
            <button
              v-for="item in dimensionOptions"
              :key="item.value"
              :class="[
                'tag-chip',
                isDimensionSelected(item.value) ? 'is-active' : '',
              ]"
              @click="toggleDimension(item.value)"
            >
              {{ item.label }}
            </button>
          </div>
        </div>

        <div class="tag-row">
          <div class="tag-label metric-label">
            <span>指标</span>
            <button class="metric-edit-btn" @click="metricDialogVisible = true">
              <i class="el-icon-edit-outline"></i>
            </button>
          </div>
          <div class="tag-list">
            <div
              v-for="(item, index) in visibleMetricSummary"
              :key="item.value"
              class="tag-chip is-active metric-chip-draggable"
              :data-metric-index="index"
              @mousedown="startMetricPointerDrag(index, $event)"
            >
              <i class="el-icon-rank metric-chip-drag-icon"></i>
              <span class="metric-chip-text">{{ item.label }}</span>
              <button
                class="metric-chip-remove"
                @mousedown.stop
                @click.stop="removeMetric(item.value)"
              >
                <i class="el-icon-close"></i>
              </button>
            </div>
            <span v-if="!visibleMetricSummary.length" class="metric-empty">
              暂无已选指标
            </span>
          </div>
        </div>
      </div>

      <div class="toolbar-footer">
        <div class="tag-label metric-label">
          <span>缓存</span>
          <el-switch v-model="queryForm.force_refresh" />
        </div>

        <div class="footer-actions">
          <el-button size="small" @click="handleReset">重置</el-button>
          <el-button
            type="primary"
            size="small"
            :loading="loading"
            @click="handleSearch"
          >
            查询
          </el-button>
        </div>
      </div>
    </section>

    <report-meta-bar :meta="metaInfo" class="meta-card" />

    <section ref="tableCard" class="report-card table-card">
      <div class="table-summary">
        <div>记录数：{{ total }}</div>
        <div>当前页：{{ pagination.page }}</div>
      </div>

      <div ref="tableShell" class="table-shell">
        <el-table
          ref="reportTable"
          :key="tableRenderKey"
          :data="tableRows"
          :height="tableHeight"
          border
          stripe
          v-loading="loading"
          empty-text="暂无报表数据"
          class="report-table"
        >
          <el-table-column
            v-for="column in tableColumns"
            :key="column.key"
            :prop="column.key"
            :label="column.label"
            :min-width="column.minWidth"
            :width="getColumnWidth(column.key)"
            :fixed="getColumnFixed(column.key)"
            show-overflow-tooltip
          >
            <template slot-scope="{ row }">
              <span v-if="column.key == 'app' && row.app">
                <el-tag>{{ row.app.name }}</el-tag>
              </span>
              <span v-else-if="column.key == 'placement' && row.placement">
                <el-tag>{{ row.placement.name }}</el-tag>
              </span>
              <span v-else-if="column.key == 'adsource' && row.adsource">
                <el-tag>{{ row.adsource.adsource_name }}</el-tag>
              </span>
              <span v-else>
                {{ formatCell(column.key, row[column.key]) }}
              </span>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <div class="pagination-row">
        <el-pagination
          background
          layout="total, prev, pager, next, sizes"
          :current-page="pagination.page"
          :page-size="pagination.pageSize"
          :page-sizes="[20, 50, 100, 200]"
          :total="total"
          @current-change="handleCurrentChange"
          @size-change="handleSizeChange"
        />
      </div>
    </section>
    </div>

    <el-dialog
      title="指标设置"
      :visible.sync="metricDialogVisible"
      width="1000px"
      class="metric-dialog"
    >
      <div class="metric-dialog__toolbar">
        <div class="metric-dialog__title">
          已选{{ selectedMetricOrder.length }}列
        </div>
        <el-button type="text" @click="clearSelectedMetrics">清空</el-button>
      </div>

      <div class="metric-dialog__body">
        <div class="metric-dialog__left">
          <div
            v-for="group in metricSettingGroups"
            :key="group.key"
            class="metric-group"
          >
            <div class="metric-group__title">{{ group.label }}</div>
            <div class="metric-group__items">
              <button
                v-for="item in group.items"
                :key="item.value"
                class="metric-item"
                :disabled="isMetricVisible(item.value)"
                @click="restoreMetric(item.value)"
              >
                {{ item.label }}
              </button>
            </div>
          </div>
        </div>

        <div class="metric-dialog__right">
          <div
            v-for="(key, index) in selectedMetricOrder"
            :key="key"
            class="selected-metric-item"
            :data-metric-index="index"
            @mousedown="startMetricPointerDrag(index, $event)"
          >
            <div class="selected-metric-item__drag">
              <i class="el-icon-rank"></i>
            </div>
            <div class="selected-metric-item__label">
              {{ getMetricLabel(key) }}
            </div>
            <button
              class="selected-metric-item__remove"
              @mousedown.stop
              @click="removeMetric(key)"
            >
              <i class="el-icon-close"></i>
            </button>
          </div>
        </div>
      </div>

      <div slot="footer" class="dialog-footer">
        <el-button @click="metricDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="metricDialogVisible = false">
          确定
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { mapState } from "vuex";
import ReportMetaBar from "./ReportMetaBar.vue";
import {
  FULL_REPORT_DIMENSION_OPTIONS,
  FULL_REPORT_FILTER_BAR_OPTIONS,
  FULL_REPORT_METRIC_OPTIONS,
  buildAppFilterOptions,
  buildFullReportColumnsWithMetricOrder,
  buildFullReportMetricSettingGroups,
  downloadCsv,
  formatDateToYYYYMMDD,
  formatReportValue,
  getDateRangeByShortcut,
  loadMetricDisplayOrder,
  saveMetricDisplayOrder,
} from "@/utils/report";

const METRIC_STORAGE_KEY = "report_full_metric_display_order_v1";

function createDefaultState() {
  const [start, end] = getDateRangeByShortcut("today");
  return {
    quickRange: "today",
    dateRange: [start.getTime(), end.getTime()],
    filters: {
      app_id_list: "",
      placement_id_list: "",
      adsource_id_list: "",
      scenario_id_list: "",
      traffic_group_id_list: "",
      adformat_list: "",
      sdk_version_list: "",
      app_version_list: "",
      area_id_list: "",
    },
    queryForm: {
      group_by: ["date", "app"],
      metric: ["all"],
      force_refresh: false,
    },
    pagination: {
      page: 1,
      pageSize: 20,
    },
    selectedApps: [],
    selectedAdFormats: [],
  };
}

export default {
  name: "ReportFull",
  components: {
    ReportMetaBar,
  },
  data() {
    const initialState = createDefaultState();
    return {
      loading: false,
      quickRange: initialState.quickRange,
      dateRange: initialState.dateRange,
      filters: initialState.filters,
      queryForm: initialState.queryForm,
      pagination: initialState.pagination,
      selectedApps: initialState.selectedApps,
      selectedAdFormats: initialState.selectedAdFormats,
      showDataShadow: false,
      tableRows: [],
      total: 0,
      tableColumns: [],
      metaInfo: {},
      tableHeight: 320,
      resizeObserver: null,
      metricDialogVisible: false,
      selectedMetricOrder: [],
      availableMetricKeys: [],
      draggingMetricIndex: -1,
      draggingMetricKey: "",
      pointerDragActive: false,
    };
  },
  computed: {
    ...mapState({
      appList: (state) => state.appList,
    }),
    quickRangeOptions() {
      return [
        { value: "today", label: "今天" },
        { value: "yesterday", label: "昨天" },
        { value: "last7", label: "过去7天" },
        { value: "last14", label: "过去14天" },
        { value: "last30", label: "过去30天" },
      ];
    },
    appOptions() {
      return buildAppFilterOptions(this.appList);
    },
    adTypeOptions() {
      return FULL_REPORT_FILTER_BAR_OPTIONS.adType;
    },
    dimensionOptions() {
      return FULL_REPORT_DIMENSION_OPTIONS;
    },
    metricOptions() {
      return FULL_REPORT_METRIC_OPTIONS.filter((item) => item.value !== "all");
    },
    metricOptionMap() {
      return this.metricOptions.reduce((result, item) => {
        result[item.value] = item;
        return result;
      }, {});
    },
    metricSettingGroups() {
      return buildFullReportMetricSettingGroups(this.metricOptions);
    },
    visibleMetricSummary() {
      return this.selectedMetricOrder
        .map((key) => this.metricOptionMap[key])
        .filter(Boolean);
    },
    tableRenderKey() {
      return JSON.stringify({
        order: this.selectedMetricOrder,
        dimensions: this.queryForm.group_by,
        available: this.availableMetricKeys,
      });
    },
  },
  methods: {
    buildRequestParams() {
      const [startdate, enddate] = this.dateRange || [];
      return {
        startdate,
        enddate,
        ...this.filters,
        app_id_list: this.selectedApps,
        adformat_list: this.selectedAdFormats,
        group_by: this.queryForm.group_by,
        metric: this.queryForm.metric,
        force_refresh: this.queryForm.force_refresh,
        page: this.pagination.page,
        pageSize: this.pagination.pageSize,
      };
    },
    applyMetricSelection(availableMetricKeys = []) {
      this.availableMetricKeys = [...availableMetricKeys];
      const fallbackOrder = this.metricOptions
        .map((item) => item.value)
        .filter((key) => availableMetricKeys.includes(key));
      const storedOrder = loadMetricDisplayOrder(METRIC_STORAGE_KEY, []);
      const nextOrder = storedOrder.length
        ? storedOrder.filter((key) => this.metricOptionMap[key])
        : fallbackOrder;
      this.selectedMetricOrder = nextOrder;
      saveMetricDisplayOrder(METRIC_STORAGE_KEY, this.selectedMetricOrder);
    },
    setMetricOrder(nextOrder = [], options = {}) {
      const { persist = true, rerender = true } = options;
      this.selectedMetricOrder = nextOrder
        .map((item) => String(item))
        .filter((item) => this.metricOptionMap[item]);
      if (persist) {
        saveMetricDisplayOrder(METRIC_STORAGE_KEY, this.selectedMetricOrder);
      }
      if (rerender) {
        this.rebuildTableColumns();
      }
    },
    rebuildTableColumns() {
      this.tableColumns = buildFullReportColumnsWithMetricOrder(
        this.tableRows,
        this.queryForm.group_by,
        this.selectedMetricOrder.filter((key) =>
          this.availableMetricKeys.includes(key)
        )
      );
      this.syncTableLayout();
    },
    async fetchReport() {
      this.loading = true;
      try {
        const payload = await this.$api.getReport.getFullReport(
          this.buildRequestParams()
        );
        this.tableRows = Array.isArray(payload.records) ? payload.records : [];
        this.total = Number(payload.count || 0);
        this.metaInfo = payload || {};
        const rawColumns = buildFullReportColumnsWithMetricOrder(
          this.tableRows,
          this.queryForm.group_by,
          []
        );
        const availableMetricKeys = rawColumns
          .map((column) => column.key)
          .filter((key) => this.metricOptionMap[key]);
        this.applyMetricSelection(availableMetricKeys);
        this.rebuildTableColumns();
      } finally {
        this.loading = false;
      }
    },
    handleQuickRangeChange(value) {
      this.quickRange = value;
      const [start, end] = getDateRangeByShortcut(value);
      this.dateRange = [start.getTime(), end.getTime()];
    },
    handleCustomDateChange() {
      this.quickRange = "";
    },
    isDimensionSelected(value) {
      return this.queryForm.group_by.includes(value);
    },
    toggleDimension(value) {
      const exists = this.isDimensionSelected(value);
      if (exists) {
        if (this.queryForm.group_by.length === 1) {
          return;
        }
        this.queryForm.group_by = this.queryForm.group_by.filter(
          (item) => item !== value
        );
      } else {
        if (this.queryForm.group_by.length >= 6) {
          this.$message.warning("group_by 最多支持 6 个维度");
          return;
        }
        this.queryForm.group_by = [...this.queryForm.group_by, value];
      }
      this.rebuildTableColumns();
    },
    handleSearch() {
      if (!this.dateRange || this.dateRange.length !== 2) {
        this.$message.warning("请选择完整日期区间");
        return;
      }
      this.pagination.page = 1;
      this.fetchReport();
    },
    handleReset() {
      const initialState = createDefaultState();
      this.quickRange = initialState.quickRange;
      this.dateRange = initialState.dateRange;
      this.filters = initialState.filters;
      this.queryForm = initialState.queryForm;
      this.pagination = initialState.pagination;
      this.selectedApps = this.appOptions.map((item) => item.value);
      this.selectedAdFormats = initialState.selectedAdFormats;
      this.showDataShadow = false;
      this.fetchReport();
    },
    handleCurrentChange(page) {
      this.pagination.page = page;
      this.fetchReport();
    },
    handleSizeChange(size) {
      this.pagination.pageSize = size;
      this.pagination.page = 1;
      this.fetchReport();
    },
    formatCell(key, value) {
      return formatReportValue(key, value, this.metaInfo);
    },
    syncTableLayout() {
      this.$nextTick(() => {
        window.requestAnimationFrame(() => {
          const shell = this.$refs.tableShell;
          const table = this.$refs.reportTable;
          if (shell) {
            this.tableHeight = Math.max(shell.clientHeight, 180);
          }
          if (table && typeof table.doLayout === "function") {
            table.doLayout();
          }
        });
      });
    },
    bindTableObserver() {
      this.syncTableLayout();
      if (typeof window !== "undefined" && "ResizeObserver" in window) {
        this.resizeObserver = new window.ResizeObserver(() => {
          this.syncTableLayout();
        });
        if (this.$refs.tableCard) {
          this.resizeObserver.observe(this.$refs.tableCard);
        }
        if (this.$refs.tableShell) {
          this.resizeObserver.observe(this.$refs.tableShell);
        }
      }
      window.addEventListener("resize", this.syncTableLayout);
    },
    unbindTableObserver() {
      window.removeEventListener("resize", this.syncTableLayout);
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }
    },
    getColumnFixed(key) {
      if (
        key === "date" ||
        key === "app" ||
        key === "app_name" ||
        key === "app_id"
      ) {
        return "left";
      }
      return false;
    },
    getColumnWidth(key) {
      if (key === "date") {
        return 120;
      }
      if (key === "app" || key === "app_name") {
        return 180;
      }
      if (key === "app_id") {
        return 120;
      }
      return undefined;
    },
    getMetricLabel(key) {
      const item = this.metricOptionMap[key];
      return item ? item.label : key;
    },
    isMetricVisible(key) {
      return this.selectedMetricOrder.includes(key);
    },
    restoreMetric(key) {
      if (this.isMetricVisible(key)) {
        return;
      }
      this.setMetricOrder([...this.selectedMetricOrder, key]);
    },
    removeMetric(key) {
      this.setMetricOrder(
        this.selectedMetricOrder.filter((item) => item !== key)
      );
    },
    clearSelectedMetrics() {
      this.setMetricOrder([]);
    },
    startMetricPointerDrag(index, event) {
      if (window.matchMedia && window.matchMedia("(max-width: 960px)").matches) {
        return;
      }
      this.draggingMetricIndex = index;
      this.draggingMetricKey = this.selectedMetricOrder[index] || "";
      this.pointerDragActive = true;
      document.addEventListener("mousemove", this.handleMetricPointerMove);
      document.addEventListener("mouseup", this.handleMetricPointerUp);
      if (event && typeof event.preventDefault === "function") {
        event.preventDefault();
      }
    },
    moveMetric(targetIndex) {
      if (
        this.draggingMetricIndex < 0 ||
        this.draggingMetricIndex === targetIndex ||
        targetIndex < 0
      ) {
        return;
      }
      const nextList = [...this.selectedMetricOrder];
      const [dragged] = nextList.splice(this.draggingMetricIndex, 1);
      nextList.splice(targetIndex, 0, dragged);
      this.setMetricOrder(nextList);
      this.draggingMetricIndex = targetIndex;
      this.draggingMetricKey = dragged;
    },
    handleMetricPointerMove(event) {
      if (!this.pointerDragActive) {
        return;
      }
      const element = document.elementFromPoint(event.clientX, event.clientY);
      const target = element ? element.closest("[data-metric-index]") : null;
      if (!target) {
        return;
      }
      const index = Number(target.getAttribute("data-metric-index"));
      if (Number.isFinite(index)) {
        this.moveMetric(index);
      }
    },
    handleMetricPointerUp() {
      this.handleMetricDragEnd();
    },
    handleMetricDragEnd() {
      document.removeEventListener("mousemove", this.handleMetricPointerMove);
      document.removeEventListener("mouseup", this.handleMetricPointerUp);
      this.draggingMetricIndex = -1;
      this.draggingMetricKey = "";
      this.pointerDragActive = false;
    },
    handleExport() {
      const dateText = formatDateToYYYYMMDD(Date.now());
      downloadCsv(
        `full-report-${dateText}.csv`,
        this.tableColumns,
        this.tableRows,
        this.metaInfo
      );
    },
  },
  created() {
    this.selectedApps = this.appOptions.map((item) => item.value);
    this.fetchReport();
  },
  mounted() {
    this.bindTableObserver();
  },
  beforeDestroy() {
    this.unbindTableObserver();
    this.handleMetricDragEnd();
  },
};
</script>

<style lang="less" scoped>
.report-page {
  min-height: 0;
  height: 100%;
  overflow: hidden;
  box-sizing: border-box;
}

.report-scroll {
  height: 100%;
  min-height: 0;
  padding-right: 4px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow-x: hidden;
  overflow-y: auto;
  box-sizing: border-box;
}

.report-card {
  padding: 20px;
  border-radius: 20px;
  background: #fff;
  box-shadow: 0 14px 28px rgba(30, 85, 187, 0.08);
}

.report-toolbar-card {
  padding: 16px 18px 18px;
}

.toolbar-top,
.toolbar-footer,
.toolbar-preview {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.toolbar-top {
  margin-bottom: 14px;
}

.quick-actions,
.toolbar-actions,
.preview-tools {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.toolbar-date {
  width: 280px;
}

.toolbar-filters {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.filter-label,
.tag-label,
.preview-title {
  min-width: 48px;
  color: #4f6389;
  font-size: 13px;
  font-weight: 600;
}

.filter-grid {
  flex: 1;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}

.toolbar-field {
  width: 100%;
}

.toolbar-field--compact /deep/ .el-input__inner,
.toolbar-field--compact /deep/ .el-select__tags {
  font-size: 12px;
}

.toolbar-field--compact /deep/ .el-input__inner {
  height: 34px;
  line-height: 34px;
}

.toolbar-field--compact /deep/ .el-tag {
  max-width: 90px;
}

.toolbar-preview,
.toolbar-tags,
.toolbar-footer {
  margin-top: 16px;
}

.preview-icon {
  width: 32px;
  height: 32px;
  border: 1px solid #dde6f5;
  border-radius: 8px;
  background: #fff;
  color: #6a84b3;
  cursor: pointer;
}

.preview-icon.is-active {
  color: #2f7dec;
  border-color: #c5dbff;
  background: #eef5ff;
}

.tag-row {
  display: flex;
  gap: 16px;
  align-items: flex-start;
}

.tag-row + .tag-row {
  margin-top: 14px;
}

.tag-list {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.tag-chip {
  border: 1px solid #dde6f5;
  background: #fff;
  color: #5b7096;
  border-radius: 8px;
  padding: 6px 12px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.tag-chip.is-active {
  color: #2f7dec;
  border-color: #c5dbff;
  background: #eef5ff;
}

.metric-label {
  display: flex;
  align-items: center;
  gap: 8px;
}

.metric-edit-btn {
  border: none;
  background: transparent;
  color: #2f7dec;
  cursor: pointer;
  font-size: 15px;
  padding: 0;
}

.metric-chip-draggable {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: move;
  padding-right: 8px;
}

.metric-chip-drag-icon {
  font-size: 12px;
  opacity: 0.7;
}

.metric-chip-text {
  line-height: 1;
}

.metric-chip-remove {
  border: none;
  background: transparent;
  color: inherit;
  cursor: pointer;
  padding: 0;
  display: inline-flex;
  align-items: center;
}

.metric-chip-remove:hover {
  color: #f56c6c;
}

.metric-empty {
  color: #97a9c8;
  font-size: 12px;
  line-height: 30px;
}

.footer-actions {
  display: flex;
  gap: 10px;
}

.meta-card {
  margin-top: 0;
}

.table-card {
  display: flex;
  flex-direction: column;
  flex: none;
  height: 520px;
  min-height: 520px;
  overflow: hidden;
}

.table-shell {
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.table-summary {
  margin-bottom: 14px;
  display: flex;
  gap: 24px;
  color: #5f7398;
  font-size: 13px;
}

.pagination-row {
  margin-top: 18px;
  display: flex;
  justify-content: flex-end;
  flex-shrink: 0;
}

.table-card /deep/ .el-table {
  width: 100%;
}

.table-card /deep/ .el-table__body-wrapper,
.table-card /deep/ .el-table__fixed-body-wrapper {
  overflow: auto !important;
}

.metric-dialog /deep/ .el-dialog__body {
  padding-top: 12px;
}

.metric-dialog__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.metric-dialog__title {
  color: #203864;
  font-size: 14px;
  font-weight: 600;
}

.metric-dialog__body {
  display: grid;
  grid-template-columns: minmax(0, 1.7fr) minmax(280px, 0.9fr);
  gap: 18px;
  min-height: 420px;
}

.metric-dialog__left,
.metric-dialog__right {
  border: 1px solid #e5ecf7;
  border-radius: 14px;
  padding: 14px;
  background: #fbfdff;
  overflow: auto;
}

.metric-group + .metric-group {
  margin-top: 16px;
}

.metric-group__title {
  margin-bottom: 10px;
  color: #203864;
  font-size: 14px;
  font-weight: 600;
}

.metric-group__items {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.metric-item {
  border: 1px solid #d9e4f6;
  background: #fff;
  color: #4f6389;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 12px;
  cursor: pointer;
}

.metric-item:disabled {
  cursor: not-allowed;
  opacity: 0.45;
}

.selected-metric-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid #d9e4f6;
  border-radius: 10px;
  background: #fff;
  cursor: move;
}

.selected-metric-item + .selected-metric-item {
  margin-top: 10px;
}

.selected-metric-item__drag {
  color: #8ba0c3;
}

.selected-metric-item__label {
  flex: 1;
  color: #203864;
  font-size: 13px;
}

.selected-metric-item__remove {
  border: none;
  background: transparent;
  color: #97a9c8;
  cursor: pointer;
}

@media (max-width: 1280px) {
  .filter-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

</style>
